import { FC, Fragment, useMemo, useState } from 'react';
import { PaginationBar } from './paginationBar';
import { TableOptions } from 'common/components/table/tableTypes';
import Loading from 'common/components/loading/loading';
import {
  ColumnResizeMode,
  getCoreRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { CustomTable } from 'common/components/table/customTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import {
  selectPaginationOptions,
  setColumnSorting
} from './paginationOptionsSlice';

export const PaginatedTable: FC<TableOptions<any>> = ({
  columns,
  data,
  componentId,
  isFetching,
  isLoading,
  getRowId,
  getRowData
}: TableOptions<any>) => {
  const memoizedData = useMemo(() => data, [data]);
  const memoizedColumns = useMemo(() => columns, [columns]);
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const dispatch = useDispatch();

  const paginationOptions = useSelector((state: RootState) =>
    selectPaginationOptions(state, componentId)
  );

  const orderDirection = paginationOptions?.orderDirection;
  const seekColumnName = paginationOptions?.seekColumnName;

  const sorting = useMemo<SortingState>(() => {
    return [
      {
        id: seekColumnName ?? '',
        desc: orderDirection === 'DESC'
      }
    ];
  }, [orderDirection, seekColumnName]);

  const setSorting = (updaterOrValue: any) => {
    const updaterValue = updaterOrValue();
    const columnName = updaterValue[0].id;
    const orderDirection = updaterValue[0].desc ? 'DESC' : 'ASC';
    dispatch(
      setColumnSorting({
        componentId,
        columnName,
        orderDirection
      })
    );
  };

  const table = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode,
    state: { sorting },
    manualSorting: true,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getRowId,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true
  });

  return (
    <Fragment key={`${componentId}-paginated-table`}>
      <div
        key={`${componentId}-table`}
        className="rounded border-1 overflow-x-hidden"
      >
        {isLoading || isFetching ? (
          <div className="p-2">
            <Loading />
          </div>
        ) : (
          <CustomTable
            table={table}
            componentId={componentId}
            heightClass="h-2/3-screen"
          />
        )}
      </div>
      <PaginationBar
        key={`${componentId}-pagination-bar`}
        componentId={componentId}
      ></PaginationBar>
    </Fragment>
  );
};
